import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import Grid from '@mui/material/Grid'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import DialogContent from '@mui/material/DialogContent'
import { useTranslation } from 'react-i18next'
import TextField from '@mui/material/TextField'
import useClasses from '../../hooks/UseClasses'
import { Button, DialogActions, ListItemText, MenuItem, useTheme, IconButton } from '@mui/material'
import apiPr from '@/api/ctmproject.api'
import apiOP from '@/api/ctmopportunity.api'
import apiPE from '@/api/entproject.api'
import GoToProjectIcon from '@/components/icons/GoToProjectIcon'
import { actions as loaderActions } from '@/actions/loader.actions'
import { useDispatch } from 'react-redux'

const styles = (theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.background.primary.color,
    color: theme.palette.text.light.color,
    padding: '8px 16px !important',
  },
  select: {
    width: '12.5rem',
    margin: '.5rem',
  },
})

const SearchAndGo = ({ open, setOpen }) => {
  const classes = useClasses(styles)
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [type, setType] = useState('PR')
  const [code, setCode] = useState(null)
  const [data, setData] = useState([])
  const [noData, setNoData] = useState(false)

  const hadleSearch = async () => {
    if (code === null || code === '') {
      return
    }
    const params = {
      sort: 'desc(idproject)',
      size: 3,
    }
    params.querysearch = ['name:contains(' + code + ')']
    let response = []
    // En estas 3 lineas await SI tiene efecto aunque el IDE diga lo contrario. Sin él, no esperará la respuesta.
    if (type === 'PR') {
      response = await dispatch(loaderActions.withGlobalLoader(() => apiPr.loadProjects(params)))
    } else if (type === 'OP') {
      params.sort = 'desc(idopportunity)'
      params.querysearch = ['code:contains(' + code + ')']
      response = await dispatch(loaderActions.withGlobalLoader(() => apiOP.loadOpportunities(params)))
    } else if (type === 'PE') {
      response = await dispatch(loaderActions.withGlobalLoader(() => apiPE.loadProjects(params)))
    }
    setData(response.data.resultList ?? [])
    setNoData(response.data.totalResult === 0)
    if (response.data.resultList !== undefined && response.data.totalResult === 1) {
      setOpen(false)
      let tuple = response.data.resultList[0].idproject
      if (type === 'OP') {
        tuple = response.data.resultList[0].idopportunity
      }
      handleGoToCtmProject(tuple)
    }
  }

  const handleGoToCtmProject = (tuple) => {
    setOpen(false)
    clearData()
    if (type === 'PR') {
      navigate('/project/' + tuple)
    } else if (type === 'OP') {
      navigate('/opportunity/' + tuple)
    } else if (type === 'PE') {
      navigate('/enterprise/project/' + tuple)
    }
    navigate(0)
  }

  const clearData = () => {
    setData([])
    setType('PR')
    setNoData(false)
  }

  return (
    <Dialog
      onClose={() => {
        setOpen(false)
        clearData()
      }}
      open={open}
      slotProps={{
        paper: {
          sx: { minHeight: '40vh' },
        },
      }}>
      <DialogTitle className={classes.dialogTitle}>
        <span style={{ textAlign: 'center' }}>
          <Typography
            variant="title_xs"
            style={{
              color: theme.palette.text.light.color,
            }}>
            {t('SEARCHGO.SEARCHER')}
          </Typography>
        </span>
      </DialogTitle>
      <IconButton
        edge="end"
        onClick={() => {
          clearData()
          setOpen(false)
        }}
        size="large"
        sx={{
          position: 'absolute',
          right: 8,
          top: 0,
        }}>
        <CloseIcon sx={{ color: 'text.light.color' }} />
      </IconButton>
      <DialogContent style={{ padding: '1.5rem' }}>
        <Grid container spacing={1}>
          <Grid size={12} style={{ marginBottom: '1rem' }}>
            <Typography
              variant={'body_s_medium'}
              style={{
                color: theme.palette.text.dark.color,
              }}>
              {t('SEARCHGO.INFO')}
            </Typography>
          </Grid>
          <Grid size={6} style={{ marginTop: '0.5rem' }}>
            <TextField
              select
              className={classes.select}
              label={t('SEARCHGO.TYPE')}
              defaultValue={'PR'}
              onChange={(event) => {
                clearData()
                setType(event.target.value)
              }}
              fullWidth>
              <MenuItem key="1" value={'PR'} selected>
                <ListItemText primary={t('SEARCHGO.TYPE.PR')} />
              </MenuItem>
              <MenuItem key="2" value={'OP'}>
                <ListItemText primary={t('SEARCHGO.TYPE.OP')} />
              </MenuItem>
              <MenuItem key="3" value={'PE'}>
                <ListItemText primary={t('SEARCHGO.TYPE.PE')} />
              </MenuItem>
            </TextField>
          </Grid>
          <Grid size={6} style={{ marginTop: '0.5rem' }}>
            <TextField
              label={t('SEARCHGO.CODE')}
              onChange={(event) => {
                setCode(event.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  hadleSearch()
                }
              }}
              fullWidth
              autoFocus
            />
          </Grid>
          {data.length > 0 && (
            <Grid size={12} style={{ marginTop: '0.5rem' }}>
              <Typography
                variant={'body_xs_medium'}
                style={{
                  color: theme.palette.text.dark.color,
                }}>
                {t('SEARCHGO.FOUNDDATA')}
              </Typography>
              {data.map((item, index) => (
                <Grid key={index}>
                  <IconButton
                    onClick={() => {
                      let tuple = item.idproject
                      if (type === 'OP') {
                        tuple = item.idopportunity
                      }
                      handleGoToCtmProject(tuple)
                    }}>
                    <GoToProjectIcon color={theme.palette.icon.accent.color} />
                    <Typography
                      variant={'body_xs_medium'}
                      style={{
                        color: theme.palette.text.dark.color,
                      }}>
                      {type === 'OP' ? item.code : item.name}
                    </Typography>
                  </IconButton>
                </Grid>
              ))}
            </Grid>
          )}
          {noData && (
            <Grid size={12} style={{ marginTop: '0.5rem', textAlign: 'center' }}>
              <Typography
                variant={'body_xs_medium'}
                style={{
                  color: theme.palette.alert.error.color,
                }}>
                {t('SEARCHGO.NODATA')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          marginBottom: '1rem',
        }}>
        <Button variant={'mediumGreen'} onClick={() => hadleSearch()}>
          {t('SEARCHGO.BUTTON')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SearchAndGo
